import type { TNpiType, TNpiTypeFormValue } from 'types/insuranceBilling';

// NPI Registry 查询结果的判定口径。NpiRegistryBox 把 NPPES 查询结果写进表单辅助字段
// （npiType / npiChecking / npiRegistryName），这里的纯函数按同一份表单值算出「这个 NPI 有什么问题」，
// 供组件内联红字、向导 Next 拦截、Review 校验共用，避免三处各写一遍判定又各自漂移。

export type TNpiIssue =
    // 非空但不足 10 位
    | 'incomplete'
    // 查询进行中（含防抖等待）
    | 'checking'
    // 还没查过：section 折叠 / 续填跳步时 NpiRegistryBox 没挂载过，表单里没有任何查询态。
    // Next 等「组件就在页面上」的场景当未通过（稍等就有结果），Review 校验放行——
    // 否则每个没展开过的 section 都会先误报一次「查不到」，展开自愈后才能再次提交
    | 'unchecked'
    | 'notfound'
    // 查到了但 NPI 类型不是该场景要求的那种（个人 NPI-1 / 机构 NPI-2）
    | 'wrongType'
    // 查到了但登记姓名与表单里填的姓名对不上
    | 'nameMismatch'
    | null;

// NPPES 登记的个人姓名；机构 NPI 不返回这两项
export interface TNpiRegistryName {
    firstName?: string;
    lastName?: string;
}

// 比对前归一化：忽略大小写、首尾/连续空格，以及点、逗号、撇号、连字符
const normalizeName = (v?: string) =>
    String(v ?? '')
        .toLowerCase()
        .replace(/[.,'’-]/g, '')
        .replace(/\s+/g, ' ')
        .trim();

// 学位/辈分后缀不参与比对：NPPES 常把它们并进姓名，表单里一般只填纯姓名
const NAME_SUFFIXES = new Set([
    'md',
    'do',
    'np',
    'pa',
    'phd',
    'psyd',
    'dnp',
    'rn',
    'lpc',
    'lcsw',
    'msw',
    'ma',
    'jr',
    'sr',
    'ii',
    'iii',
    'iv',
]);

const cleanName = (v?: string) =>
    normalizeName(v)
        .split(' ')
        .filter((t) => t && !NAME_SUFFIXES.has(t))
        .join(' ');

// 单项（名或姓）比对。任一侧为空都不判：接口没返回、用户还没填，都不该在这里报错
const partMismatch = (registry?: string, expected?: string) => {
    const a = cleanName(registry);
    const b = cleanName(expected);
    if (!a || !b) return false;
    if (a === b) return false;
    // 连字符姓（"Smith-Jones" vs "Smith Jones"）归一化后只差一个空格
    if (a.replace(/ /g, '') === b.replace(/ /g, '')) return false;
    // NPPES 的 firstName 常把 middle name 并在一起（"Mary Jane"），首词一致即认可
    return a.split(' ')[0] !== b.split(' ')[0];
};

// 登记姓名与表单姓名是否对不上。后端没返回姓名（机构 NPI）时整项跳过，不校验
export const npiNameMismatch = (registry?: TNpiRegistryName, expected?: TNpiRegistryName): boolean => {
    if (!registry?.firstName && !registry?.lastName) return false;
    return partMismatch(registry.firstName, expected?.firstName) || partMismatch(registry.lastName, expected?.lastName);
};

export interface TNpiCheckOptions {
    // NPI 在 slot 里的字段名；group enrollment 那份表单叫 groupNpi
    npiKey?: string;
    // 该场景要求的 NPI 类型；不传则不校验类型
    expectType?: TNpiType;
    // 要与登记姓名比对的表单姓名；不传则不校验姓名（practice NPI 不比对）
    expectedName?: TNpiRegistryName;
}

// slot = NPI 所在的那一层表单值（credentialing 基本信息是整份 values，group 是 enrollment 对象）
export const npiIssue = (slot: Record<string, any> | undefined, options: TNpiCheckOptions = {}): TNpiIssue => {
    const { npiKey = 'npi', expectType, expectedName } = options;
    const digits = String(slot?.[npiKey] ?? '').replace(/\D/g, '');
    // 没填：由各自的 required 规则管，不归这里判
    if (!digits) return null;
    if (digits.length !== 10) return 'incomplete';
    if (slot?.npiChecking) return 'checking';
    const npiType = slot?.npiType as TNpiTypeFormValue | undefined;
    if (!npiType) return 'unchecked';
    if (npiType === 'notfound') return 'notfound';
    if (expectType && npiType !== expectType) return 'wrongType';
    if (expectedName && npiNameMismatch(slot?.npiRegistryName, expectedName)) return 'nameMismatch';
    return null;
};

// Review 校验口径：没查过不算错（组件没挂载过，展开 section 时会自动补查）
export const npiIssueBlocksReview = (issue: TNpiIssue) => issue !== null && issue !== 'unchecked';

// 统一文案。出于隐私考虑，姓名不匹配时不回显 NPI Registry 上登记的姓名
export const npiIssueText = (issue: TNpiIssue, expectType?: TNpiType): string => {
    switch (issue) {
        case 'incomplete':
            return 'NPI must be 10 digits.';
        case 'checking':
        case 'unchecked':
            return 'Checking the NPI Registry…';
        case 'notfound':
            return 'We could not find this NPI in the NPI Registry. Double check the 10 digits and try again.';
        case 'wrongType':
            return expectType === 'Type 2'
                ? "This is an Individual NPI (NPI-1). Please enter your practice's Organization NPI (NPI-2)."
                : 'This is an Organization NPI (NPI-2). Please enter an Individual NPI (NPI-1).';
        case 'nameMismatch':
            return 'This NPI is registered under a different name. Check the NPI, or make sure the name you entered matches the NPI Registry.';
        default:
            return '';
    }
};
